import React, { memo, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const Item = ({ icon, label, onClick }) => (
  <button type="button" className="ws-context-item" onClick={onClick}>
    {icon}
    <span>{label}</span>
  </button>
);

/**
 * WorkspaceContextMenu
 * Right-click actions for an app card: Pin/Unpin, Hide/Show, Open,
 * Open in New Tab, Move to top. Positioned at the cursor and clamped to the
 * viewport. Closes on outside click, scroll or Escape.
 */
const WorkspaceContextMenu = ({ menu, onClose, onOpen, onOpenNewTab, onToggleFavorite, onToggleHidden, onMoveTop }) => {
  const ref = useRef(null);
  const [pos, setPos] = useState({ x: menu.x, y: menu.y });

  useEffect(() => {
    // Clamp to viewport once the menu has a measured size.
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = Math.min(menu.x, window.innerWidth - rect.width - 8);
    const y = Math.min(menu.y, window.innerHeight - rect.height - 8);
    setPos({ x: Math.max(8, x), y: Math.max(8, y) });
  }, [menu.x, menu.y]);

  useEffect(() => {
    const handleDown = (e) => {
      if (!ref.current?.contains(e.target)) onClose();
    };
    const handleKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', handleDown);
    document.addEventListener('scroll', onClose, true);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleDown);
      document.removeEventListener('scroll', onClose, true);
      document.removeEventListener('keydown', handleKey);
    };
  }, [onClose]);

  const app = menu.app;
  const run = (fn) => () => {
    fn(app);
    onClose();
  };

  return createPortal(
    <div className="ws-context-menu" ref={ref} style={{ left: pos.x, top: pos.y }} role="menu">
      <Item
        label="Open"
        onClick={run(onOpen)}
        icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" /></svg>}
      />
      <Item
        label="Open in new tab"
        onClick={run(onOpenNewTab)}
        icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M19 19H5V5h7V3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2v-7h-2v7zM14 3v2h3.59l-9.83 9.83 1.41 1.41L19 6.41V10h2V3h-7z" /></svg>}
      />
      <div className="ws-context-divider" />
      <Item
        label={app.isFavorite ? 'Unpin' : 'Pin'}
        onClick={run(onToggleFavorite)}
        icon={<svg width="16" height="16" viewBox="0 0 24 24" fill={app.isFavorite ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2"><path d="M12 2l2.9 6.26 6.1.55-4.6 4.24 1.36 6.4L12 16.9 6.24 19.9l1.36-6.4L3 9.27l6.1-.55L12 2z" /></svg>}
      />
      <Item
        label={app.isHidden ? 'Show' : 'Hide'}
        onClick={run(onToggleHidden)}
        icon={
          app.isHidden ? (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zm0 12a4.5 4.5 0 110-9 4.5 4.5 0 010 9zm0-7a2.5 2.5 0 100 5 2.5 2.5 0 000-5z" /></svg>
          ) : (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46C3.08 8.3 1.78 10.02 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27z" /></svg>
          )
        }
      />
      {!app.isFavorite && (
        <Item
          label="Move to top"
          onClick={run(onMoveTop)}
          icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M4 4h16v2H4zm8 4l6 6h-4v6h-4v-6H6z" /></svg>}
        />
      )}
    </div>,
    document.body
  );
};

export default memo(WorkspaceContextMenu);
